import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handlePhotoSelection,
      { signal }
    );
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
#pendingPhotoVariantId = null;

  #handlePhotoSelection = (event) => {
    const detail = event.detail;

    // Ignorer les repositionnements automatiques et les autres carrousels.
    if (!detail?.userInitiated) return;
    if (event.target !== this.slideshow) return;
    if (!(detail.slide instanceof Element)) return;

    const mediaId = detail.slide
      .querySelector('[data-mm-media-id]')
      ?.getAttribute('data-mm-media-id');

    const dataElement = this.querySelector(
      'script[data-mm-gallery-variants]'
    );

    if (!mediaId || !dataElement?.textContent) return;

    let galleryData;

    try {
      galleryData = JSON.parse(dataElement.textContent);
    } catch {
      return;
    }

    // Première version : produits avec une seule option.
    if (galleryData.optionCount !== 1) return;

    const matches = galleryData.variants.filter(
      (variant) =>
        variant.mediaId != null &&
        String(variant.mediaId) === mediaId
    );

    // Ne jamais choisir arbitrairement parmi plusieurs variantes.
    if (matches.length !== 1) return;

    const variant = matches[0];
    const section = this.closest('.shopify-section');
    if (!section) return;

    const picker = Array.from(
      section.querySelectorAll('variant-picker')
    ).find(
      (element) =>
        element.dataset.productId === String(galleryData.productId) &&
        element.dataset.templateProductMatch === 'true' &&
        !element.closest('product-card, quick-add-dialog')
    );

    if (!picker) return;

    const radio = Array.from(
      picker.querySelectorAll('fieldset input[type="radio"]')
    ).find((input) => input.value === variant.option);

    if (radio) {
      if (radio.checked || radio.disabled) return;

      this.#pendingPhotoVariantId = String(variant.id);
      radio.click();
      return;
    }

    // Prendre également en charge un sélecteur déroulant.
    const select = picker.querySelector('select');
    if (!select || select.disabled) return;

    const option = Array.from(select.options).find(
      (item) => item.value === variant.option
    );

    if (!option || option.disabled || select.value === option.value) return;

    this.#pendingPhotoVariantId = String(variant.id);
    select.value = option.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };
  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
   #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const variantElement = source.querySelector(
      'variant-picker script[type="application/json"]'
    );

    let updatedVariantId = null;

    try {
      if (variantElement?.textContent) {
        const variant = JSON.parse(variantElement.textContent);
        if (variant?.id != null) {
          updatedVariantId = String(variant.id);
        }
      }
    } catch {
      // En cas de données illisibles, conserver le fonctionnement normal.
    }

    const keepCurrentGallery =
      this.#pendingPhotoVariantId !== null &&
      updatedVariantId === this.#pendingPhotoVariantId;

    this.#pendingPhotoVariantId = null;

    // Le prix et le formulaire continuent de recevoir la mise à jour,
    // mais la galerie ne revient pas au début après un défilement.
    if (keepCurrentGallery) return;

    // Un clic direct sur une déclinaison conserve le comportement habituel.
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
